import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Upload, FileSpreadsheet, X, CheckCircle2, AlertCircle, 
  User, Mail, Phone, DollarSign, Percent, Users, Building2,
  Plane, MapPin, Calendar, Loader2, ChevronRight, ChevronDown,
  FileText, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = ['EUR', 'USD', 'GBP', 'INR', 'AUD', 'CAD'];

const ExcelImportModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  // States
  const [step, setStep] = useState(1); // 1: Upload, 2: Review & Configure
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [showCostDetails, setShowCostDetails] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // User input states
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    currency: 'EUR',
    sale_value: '',
    contingency_percent: 0,
    guest_handling: 'combine' // 'combine', 'primary', 'custom'
  });

  const resetModal = () => {
    setStep(1);
    setSelectedFile(null);
    setParsedData(null);
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      currency: 'EUR',
      sale_value: '',
      contingency_percent: 0,
      guest_handling: 'combine'
    });
    setShowCostDetails(false);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const validateFile = (file) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExtension = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );
    if (!hasValidExtension) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return false;
    }
    return true;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async (useAI = false) => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formDataObj = new FormData();
      formDataObj.append('file', selectedFile);

      // Use the precise trip report parser with optional AI
      const url = useAI 
        ? `${API_URL}/api/import/parse-trip-report?use_ai=true`
        : `${API_URL}/api/import/parse-trip-report`;
        
      const response = await axios.post(url, formDataObj, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const report = response.data.parsed_report;
        setParsedData({
          ...report,
          ai_used: response.data.ai_used,
          parsing_method: response.data.parsing_method
        });
        
        // Pre-fill form with extracted data
        let clientName = '';
        if (report.guest_details?.guest_names?.length > 0) {
          clientName = report.guest_details.guest_names.join(' & ');
        }
        
        // Only pre-fill email if source_contact looks like an email
        const sourceContact = report.guest_details?.source_contact || '';
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sourceContact);
        
        setFormData(prev => ({
          ...prev,
          client_name: clientName,
          currency: report.financials?.currency || 'EUR',
          sale_value: report.financials?.total_sale_value?.toString() || '',
          client_email: isEmail ? sourceContact : '',
        }));
        
        setStep(2);
        
        // Show verification status with parsing method
        const aiNote = response.data.ai_used ? ' (AI assisted)' : '';
        if (response.data.verification_status === 'VERIFIED') {
          toast.success(`File parsed and verified successfully!${aiNote}`);
        } else if (response.data.verification_status === 'NO_DATA') {
          toast.warning('No trip data found in file. Please check the file format.');
        } else {
          toast.warning(`File parsed - some totals may not match. Please review.${aiNote}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
    } finally {
      setUploading(false);
    }
  };

  const handleGuestHandlingChange = (value) => {
    setFormData(prev => ({ ...prev, guest_handling: value }));
    
    const guestNames = parsedData?.guest_details?.guest_names || [];
    if (guestNames.length > 0) {
      let clientName = '';
      switch (value) {
        case 'combine':
          clientName = guestNames.join(' & ');
          break;
        case 'primary':
          clientName = guestNames[0];
          break;
        case 'custom':
          clientName = formData.client_name;
          break;
        default:
          clientName = guestNames.join(' & ');
      }
      setFormData(prev => ({ ...prev, client_name: clientName }));
    }
  };

  const buildCostElements = () => {
    const elements = [];
    const costItems = parsedData?.cost_items || {};
    const financials = parsedData?.financials || {};
    
    // Add individual hotel line items
    if (costItems.hotels?.length > 0) {
      costItems.hotels.forEach((hotel) => {
        const cost = hotel.net_paid || hotel.net_payable || 0;
        if (cost > 0) {
          elements.push({
            expense_type: 'Accommodation',
            service_provider: hotel.name || 'Hotel',
            value: cost,
            location: null
          });
        }
      });
    }
    
    // Add individual activity line items
    if (costItems.activities?.length > 0) {
      costItems.activities.forEach((activity) => {
        const cost = activity.net_paid || activity.booked_price || 0;
        if (cost > 0) {
          elements.push({
            expense_type: 'Adventure – Safari',
            service_provider: activity.name || 'Activity',
            value: cost,
            location: null
          });
        }
      });
    }
    
    // Add individual transfer line items
    if (costItems.transfers?.length > 0) {
      costItems.transfers.forEach((transfer) => {
        const cost = transfer.net_paid || transfer.booked_price || 0;
        if (cost > 0) {
          elements.push({
            expense_type: 'Transportation',
            service_provider: transfer.name || 'Transfer',
            value: cost,
            location: null
          });
        }
      });
    }
    
    // Fallback: Use summary totals if no line items
    if (elements.length === 0) {
      const componentTotals = financials.component_totals || {};
      
      if (componentTotals.hotels > 0) {
        elements.push({
          expense_type: 'Accommodation',
          service_provider: 'Hotels (Total)',
          value: componentTotals.hotels,
          location: null
        });
      }
      
      if (componentTotals.transports > 0) {
        elements.push({
          expense_type: 'Transportation',
          service_provider: 'Transfers (Total)',
          value: componentTotals.transports,
          location: null
        });
      }
      
      if (componentTotals.activities > 0) {
        elements.push({
          expense_type: 'Adventure – Safari',
          service_provider: 'Activities (Total)',
          value: componentTotals.activities,
          location: null
        });
      }
    }
    
    return elements;
  };

  const calculateTotalCost = () => {
    const elements = buildCostElements();
    return elements.reduce((sum, el) => sum + (el.value || 0), 0);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCreateTour = async () => {
    // Validation
    if (!formData.client_name) {
      toast.error('Please enter client name');
      return;
    }
    if (!formData.client_email) {
      toast.error('Please enter client email');
      return;
    }
    if (!isValidEmail(formData.client_email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!formData.client_phone) {
      toast.error('Please enter client phone');
      return;
    }
    if (!formData.sale_value || parseFloat(formData.sale_value) <= 0) {
      toast.error('Please enter valid sale value');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      const payload = {
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        currency: formData.currency,
        sale_value: parseFloat(formData.sale_value),
        contingency_percent: parseFloat(formData.contingency_percent) || 0,
        cost_elements: buildCostElements()
      };

      const response = await axios.post(`${API_URL}/api/import/create-tour`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Tour package created successfully!');
        onSuccess?.(response.data.package);
        handleClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tour');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="excel-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            Import Tour from Excel/CSV
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Upload your trip report file to import tour data'}
            {step === 2 && 'Review extracted data and provide missing information'}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 py-2">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-primary text-white' : 'bg-muted'}`}>
              1
            </div>
            <span className="text-sm font-medium">Upload</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-primary text-white' : 'bg-muted'}`}>
              2
            </div>
            <span className="text-sm font-medium">Review & Configure</span>
          </div>
        </div>

        {/* Step 1: File Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'hover:border-primary'
              }`}
              data-testid="import-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                data-testid="import-file-input"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="w-12 h-12 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-lg font-medium mb-1">Drop your file here or click to browse</p>
                  <p className="text-sm text-muted-foreground">
                    Supports Excel (.xlsx, .xls) and CSV files
                  </p>
                </>
              )}
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Supported Report Formats
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Trip reports from travel management software</li>
                <li>• Booking confirmations with cost breakdowns</li>
                <li>• Itinerary exports with pricing details</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Review & Configure */}
        {step === 2 && parsedData && (
          <div className="space-y-6">
            {/* Verification Status Banner */}
            {parsedData.financials?.verification && (
              <div
                data-testid="verification-status-banner"
                data-verified={parsedData.financials.verification.is_verified ? 'true' : 'false'}
                className={`p-3 rounded-lg border ${
                parsedData.financials.verification.is_verified 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-2">
                  {parsedData.financials.verification.is_verified ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">All totals verified and match!</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-amber-800">Some totals don't match - please review</span>
                    </>
                  )}
                </div>
                {parsedData.financials.verification.errors?.length > 0 && (
                  <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                    {parsedData.financials.verification.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Extracted Data Preview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2" data-testid="extracted-data-title">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Extracted Data ({parsedData.summary?.total_line_items || 0} line items)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {parsedData.trip_details?.destinations?.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Destinations:</span>
                      <p className="font-medium">{parsedData.trip_details.destinations.join(', ')}</p>
                    </div>
                  )}
                  {parsedData.guest_details?.guest_names?.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Guests Found:</span>
                      <p className="font-medium">{parsedData.guest_details.guest_names.join(', ')}</p>
                    </div>
                  )}
                  {parsedData.financials?.total_sale_value > 0 && (
                    <div>
                      <span className="text-muted-foreground">Sale Value:</span>
                      <p className="font-medium text-green-600">
                        {parsedData.financials.currency} {parsedData.financials.total_sale_value.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {parsedData.financials?.total_cost > 0 && (
                    <div>
                      <span className="text-muted-foreground">Total Cost:</span>
                      <p className="font-medium">
                        {parsedData.financials.currency} {parsedData.financials.total_cost.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {parsedData.financials?.profit > 0 && (
                    <div>
                      <span className="text-muted-foreground">Profit:</span>
                      <p className="font-medium text-emerald-600">
                        {parsedData.financials.currency} {parsedData.financials.profit.toLocaleString()} 
                        ({((parsedData.financials.profit / parsedData.financials.total_sale_value) * 100).toFixed(1)}%)
                      </p>
                    </div>
                  )}
                </div>

                {/* Cost Breakdown */}
                {parsedData.financials?.component_totals && (
                  <div className="border-t pt-3 mt-3">
                    <button 
                      data-testid="cost-breakdown-toggle"
                      className="flex items-center gap-2 text-sm font-medium w-full"
                      onClick={() => setShowCostDetails(!showCostDetails)}
                    >
                      {showCostDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      Cost Breakdown ({parsedData.summary?.total_hotels || 0} Hotels, {parsedData.summary?.total_activities || 0} Activities, {parsedData.summary?.total_transfers || 0} Transfers)
                      {parsedData.financials?.is_multi_currency && (
                        <Badge variant="outline" className="ml-2 text-xs">Multi-Currency</Badge>
                      )}
                    </button>
                    {showCostDetails && (
                      <>
                        {/* Multi-currency breakdown */}
                        {parsedData.financials?.is_multi_currency && parsedData.financials?.currency_totals && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm font-medium text-amber-800 mb-2">
                              Currencies detected: {parsedData.financials.currencies_detected?.join(', ')}
                            </p>
                            <div className="space-y-2">
                              {Object.entries(parsedData.financials.currency_totals).map(([curr, totals]) => (
                                <div key={curr} className="flex items-center justify-between text-sm">
                                  <span className="font-medium">{curr}</span>
                                  <span>
                                    Hotels: {totals.hotels?.toLocaleString()} | 
                                    Transfers: {totals.transports?.toLocaleString()} | 
                                    Activities: {totals.activities?.toLocaleString()} | 
                                    <span className="font-semibold ml-1">Total: {totals.total?.toLocaleString()}</span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Standard breakdown cards */}
                        <div className="mt-2 grid grid-cols-3 gap-3">
                          {parsedData.financials.component_totals.hotels > 0 && (
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <Building2 className="w-4 h-4 text-blue-600 mb-1" />
                              <p className="text-xs text-blue-600">Hotels ({parsedData.summary?.total_hotels || 0})</p>
                              <p className="font-semibold">{parsedData.financials.currency} {parsedData.financials.component_totals.hotels.toLocaleString()}</p>
                            </div>
                          )}
                          {parsedData.financials.component_totals.transports > 0 && (
                            <div className="bg-purple-50 p-3 rounded-lg">
                              <Plane className="w-4 h-4 text-purple-600 mb-1" />
                              <p className="text-xs text-purple-600">Transfers ({parsedData.summary?.total_transfers || 0})</p>
                              <p className="font-semibold">{parsedData.financials.currency} {parsedData.financials.component_totals.transports.toLocaleString()}</p>
                            </div>
                          )}
                          {parsedData.financials.component_totals.activities > 0 && (
                            <div className="bg-green-50 p-3 rounded-lg">
                              <MapPin className="w-4 h-4 text-green-600 mb-1" />
                              <p className="text-xs text-green-600">Activities ({parsedData.summary?.total_activities || 0})</p>
                              <p className="font-semibold">{parsedData.financials.currency} {parsedData.financials.component_totals.activities.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* User Input Required */}
            <Card className="border-primary/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Required Information
                </CardTitle>
                <CardDescription>Please provide the following details to create the tour</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Guest Handling */}
                {parsedData.guest_details?.guest_names?.length > 1 && (
                  <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Multiple Guests Detected
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Found: {parsedData.guest_details.guest_names.join(', ')}
                    </p>
                    <RadioGroup
                      value={formData.guest_handling}
                      onValueChange={handleGuestHandlingChange}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="combine" id="combine" data-testid="guest-handling-combine" />
                        <Label htmlFor="combine" className="font-normal cursor-pointer">
                          Combine all: "{parsedData.guest_details.guest_names.join(' & ')}"
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="primary" id="primary" data-testid="guest-handling-primary" />
                        <Label htmlFor="primary" className="font-normal cursor-pointer">
                          Use primary guest only: "{parsedData.guest_details.guest_names[0]}"
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="custom" id="custom" data-testid="guest-handling-custom" />
                        <Label htmlFor="custom" className="font-normal cursor-pointer">
                          Enter custom name
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {/* Client Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_name" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Client Name *
                    </Label>
                    <Input
                      id="client_name"
                      data-testid="import-client-name-input"
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Enter client name"
                      disabled={formData.guest_handling !== 'custom' && parsedData.guest_names?.length > 1}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="client_email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Primary Contact Email *
                    </Label>
                    <Input
                      id="client_email"
                      data-testid="import-client-email-input"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="client@example.com"
                    />
                    <p className="text-xs text-muted-foreground">
                      This email will receive TourPilot documents and invoices
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="client_phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Client Phone *
                    </Label>
                    <Input
                      id="client_phone"
                      data-testid="import-client-phone-input"
                      value={formData.client_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="+358 40 123 4567"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Financial */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="sale_value" className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Sale Value ({formData.currency}) *
                    </Label>
                    <Input
                      id="sale_value"
                      data-testid="import-sale-value-input"
                      type="number"
                      step="0.01"
                      value={formData.sale_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, sale_value: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contingency" className="flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Contingency (%)
                    </Label>
                    <Input
                      id="contingency"
                      data-testid="import-contingency-input"
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={formData.contingency_percent}
                      onChange={(e) => setFormData(prev => ({ ...prev, contingency_percent: e.target.value }))}
                      placeholder="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional buffer for unexpected costs
                    </p>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-800 mb-2">Tour Summary</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-green-600">Sale Value</p>
                      <p className="font-bold text-green-800">
                        {formData.currency} {parseFloat(formData.sale_value || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-600">Imported Costs</p>
                      <p className="font-bold text-green-800">
                        {formData.currency} {calculateTotalCost().toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-600">Contingency</p>
                      <p className="font-bold text-green-800">
                        {formData.contingency_percent}%
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose} data-testid="import-cancel-button">
                Cancel
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleUpload(true)} 
                disabled={!selectedFile || uploading}
                data-testid="parse-ai-button"
                title="Uses AI credits for parsing"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded mr-2">AI</span>
                )}
                Parse with AI
              </Button>
              <Button onClick={() => handleUpload(false)} disabled={!selectedFile || uploading} data-testid="parse-file-button">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    Parse File
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </>
          )}
          
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="import-back-button">
                Back
              </Button>
              <Button variant="outline" onClick={handleClose} data-testid="import-cancel-button">
                Cancel
              </Button>
              <Button onClick={handleCreateTour} disabled={creating} data-testid="create-tour-button">
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Create Tour Package
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExcelImportModal;
